import { ReactElement } from "react";
import { Link as RouterLink } from "react-router-dom";
// @mui
import {
  Box,
  BreadcrumbsProps,
  Link,
  Breadcrumbs as MUIBreadcrumbs,
  Stack,
  Typography,
} from "@mui/material";
import SvgIconStyle from "./SvgIconStyle";

// ----------------------------------------------------------------------

type TLink = {
  href?: string;
  name: string;
  icon?: ReactElement;
};

export interface Props extends BreadcrumbsProps {
  links: TLink[];
  activeLast?: boolean;
  homeIcon: string;
}

export default function Breadcrumbs({
  links,
  homeIcon,
  activeLast = false,
  ...other
}: Props) {
  const currentLink = links[links.length - 1].name;

  const listDefault = links.map((link) => (
    <LinkItem key={link.name} link={link} />
  ));

  const listActiveLast = links.map((link) => (
    <div key={link.name}>
      {link.name !== currentLink ? (
        <LinkItem link={link} />
      ) : (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 260,
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: "text.disabled",
            textOverflow: "ellipsis",
          }}
        >
          {currentLink}
        </Typography>
      )}
    </div>
  ));

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ display: "flex", alignItems: "center" }}
    >
      <SvgIconStyle
        src={`/assets/icons/navbar/${homeIcon}.svg`}
        sx={{ width: 20, height: 20 }}
      />
      <SvgIconStyle
        src="/assets/icons/header/chevron.svg"
        sx={{ width: 16, height: 16, opacity: 0.25, paddingX: 2 }}
      />
      <MUIBreadcrumbs
        separator={
          <SvgIconStyle
            src="/assets/icons/header/chevron.svg"
            sx={{ width: 16, height: 16, opacity: 0.25 }}
          />
        }
        {...other}
      >
        {activeLast ? listDefault : listActiveLast}
      </MUIBreadcrumbs>
    </Stack>
  );
}

// ----------------------------------------------------------------------

type LinkItemProps = {
  link: TLink;
};

function LinkItem({ link }: LinkItemProps) {
  const { href, name, icon } = link;
  return (
    <Link
      key={name}
      variant="body2"
      component={RouterLink}
      to={href || "#"}
      sx={{
        lineHeight: 2,
        display: "flex",
        alignItems: "center",
        color: "text.primary",
        "& > div": { display: "inherit" },
      }}
    >
      {icon && (
        <Box sx={{ mr: 1, "& svg": { width: 20, height: 20 } }}>{icon}</Box>
      )}
      {name}
    </Link>
  );
}
